/**
 * `ChatbotDock` — Component 7 of the help-docs-panel design.
 *
 * The 360 × 520 focus-trapped chat dialog anchored at
 * `bottom: 96px; right: 24px; z-index: 46`. Wraps its body in a
 * {@link FocusTrap} from `focus-trap-react` so keyboard focus cycles
 * inside the dock and `Esc` closes it, returning focus to the
 * `[data-chatbot-fab]` launcher.
 *
 * ## Behaviour contract
 *
 * - `role="dialog"`, `aria-modal="false"` (the dock overlays but does not
 *   block the underlying page), accessible name "Ledgiventa assistant"
 *   via `aria-labelledby` on a hidden `<h2>` (Requirement 8.1).
 * - On the `open === false → true` transition, focus is moved to the
 *   send button when {@link DocsSliderContext.errorId} is set, otherwise
 *   to the message input. An "Chatbot opened. Type your question."
 *   announcement is emitted via {@link AriaLive} (Requirement 8.2).
 * - On the `open === true → false` transition, "Chatbot closed" is
 *   announced (Requirement 8.5) and — because the trap has been
 *   deactivated — focus returns to the `[data-chatbot-fab]` element
 *   (Requirement 8.4). When there is no such element in the DOM (rare —
 *   the FAB is always mounted alongside the dock in the production
 *   provider) `focus-trap-react` falls back to the previously-focused
 *   element.
 * - When `context.errorId` is set on open, the message input is
 *   pre-filled with the exact string
 *   `` `I need help with error ${context.errorId}.` `` (Requirement 10.2).
 * - Assistant messages carrying `citedDocs[]` render one {@link CitationChip}
 *   per path. Clicking a chip invokes `openDocsAt(path)` — the dock
 *   itself stays open (Requirement 8.6).
 * - When the user's message is a bare Error ID (nothing else on the line),
 *   the dock resolves it against the owner-scoped `GET /errors/{errorId}`
 *   endpoint via {@link lookupErrorById} INSTEAD of the chatbot transport,
 *   and renders the stored record. This keeps the transport tenant-isolated
 *   (Constitution Rule II) while still letting the assistant answer "what
 *   does this Error ID mean?" (Requirement 11).
 * - The message form NEVER holds `disabled` on the send button so
 *   `focus-trap-react` can always focus it; the empty/sending guard is
 *   enforced inside `handleSubmit` and communicated via `aria-disabled`.
 *
 * ## Props contract (Wave 2.7 isolated surface)
 *
 * Wave 2.1 (`DocsSliderProvider`) is not yet implemented; this component
 * is designed to be composed by that provider without changes. It accepts
 * an explicit prop surface — `open`, `onClose`, `context`, `transport`,
 * `sessionId` — so it can also be exercised in isolation from tests.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirements 8.1–8.6, 10.2, 10.5
 * @see .kiro/specs/help-docs-panel/design.md — Component 7 "ChatbotDock"
 * @see ./AriaLive.tsx — the polite live region used for open/close announcements
 * @see ./CitationChip.tsx — the citation chip rendered per `citedDocs[]` entry
 * @see ./StubChatbotTransport.ts — the {@link ChatbotTransport} type / stub impl
 *
 * @packageDocumentation
 */
import { FocusTrap } from "focus-trap-react";
import * as React from "react";
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";

import { AriaLive } from "./AriaLive";
import { CitationChip } from "./CitationChip";
import { lookupErrorById, parseErrorIdQuery } from "./errorLookup";
import type { ChatbotResponse, ChatbotTransport } from "./StubChatbotTransport";
import type { DocsSliderContext } from "./types";

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * Props accepted by {@link ChatbotDock}.
 *
 * @remarks
 * `DocsSliderProvider` (Wave 2.1) will pass these through from its own
 * context state. Meanwhile, tests and any isolated composer pass them
 * directly.
 */
export interface ChatbotDockProps {
  /**
   * Whether the dock is currently open. When `false` the dialog is
   * unmounted (only the {@link AriaLive} region remains, so the "closed"
   * announcement is still audible to screen readers).
   */
  open: boolean;

  /**
   * Invoked when the dock deactivates — either by the user pressing
   * `Esc` (via `focus-trap-react`) or clicking the header's close
   * button. The parent MUST flip `open` to `false` in response.
   */
  onClose(): void;

  /**
   * Optional {@link DocsSliderContext} — when `errorId` is set on the
   * open transition, the message input is pre-filled and the send
   * button receives initial focus.
   */
  context?: DocsSliderContext;

  /**
   * The {@link ChatbotTransport} implementation used to send messages.
   * `StubChatbotTransport` today; the real MCP client in the deferred
   * `chatbot-mcp` spec (P13).
   */
  transport: ChatbotTransport;

  /**
   * A hashed session identifier (16 lowercase hex characters, produced
   * by `hashSessionKey`). Passed through to `transport.send()` — the
   * transport never sees the raw `ownerId` (Constitution Rule II).
   */
  sessionId: string;

  /**
   * Optional override for the Error-ID resolver used when the user types
   * a bare Error ID (Requirement 11). Defaults to {@link lookupErrorById},
   * which calls the owner-scoped `GET /errors/{errorId}` endpoint via the
   * authenticated `api` client. Injectable so tests can exercise the
   * interception path without a real HTTP call.
   */
  errorLookup?: (errorId: string) => Promise<ChatbotResponse>;
}

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

/** Announced on the open transition (Requirement 8.2). */
const OPEN_ANNOUNCEMENT = "Chatbot opened. Type your question.";

/** Announced on the close transition (Requirement 8.5). */
const CLOSE_ANNOUNCEMENT = "Chatbot closed";

/** Selectors passed to `focus-trap-react` for initial and return focus. */
const MESSAGE_INPUT_SELECTOR = "[data-chatbot-message-input]";
const SEND_BUTTON_SELECTOR = "[data-chatbot-send-button]";
const FAB_SELECTOR = "[data-chatbot-fab]";

// ---------------------------------------------------------------------------
// Message model (in-memory only — persistence is Wave 3+ scope)
// ---------------------------------------------------------------------------

/**
 * A single message rendered in the dock's transcript.
 *
 * @remarks
 * - `id` is a monotonically-increasing counter (not a timestamp) so
 *   rapid successive messages never collide on the React `key`.
 * - `citedDocs` is only meaningful on assistant messages; user messages
 *   never carry citations.
 */
interface ChatMessage {
  readonly id: number;
  readonly role: "user" | "assistant";
  readonly text: string;
  readonly citedDocs?: readonly string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Focus-trapped chat dialog. Returns `null` semantically only when
 * `open === false` and no announcement has been emitted yet — the
 * {@link AriaLive} region remains mounted so a subsequent close
 * transition is still announceable.
 */
export function ChatbotDock(props: ChatbotDockProps): JSX.Element {
  const {
    open,
    onClose,
    context,
    transport,
    sessionId,
    errorLookup = lookupErrorById,
  } = props;
  const titleId = useId();
  const errorId = context?.errorId;

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState<boolean>(false);
  const messageIdRef = useRef<number>(0);

  // Lazy initial state so the first render (before FocusTrap mounts)
  // already carries the pre-filled input when `open` starts `true` with
  // a defined `errorId`. This matters because `focus-trap-react`'s
  // initialFocus fires synchronously with FocusTrap mount, and a
  // disabled-looking send button (empty input) would be un-focusable
  // if we set `disabled` — we avoid `disabled` entirely, but the state
  // must still be correct at first paint for the announcement text.
  const [input, setInput] = useState<string>(() =>
    open && errorId !== undefined ? `I need help with error ${errorId}.` : "",
  );
  const [announcement, setAnnouncement] = useState<string>(() =>
    open ? OPEN_ANNOUNCEMENT : "",
  );

  // ------------------------------------------------------------------
  // Derived-state pattern (React 18 "adjusting state during render").
  // Runs BEFORE FocusTrap mounts, so `input` is pre-filled and the
  // announcement is set in time for the same commit.
  // ------------------------------------------------------------------

  const [prevOpen, setPrevOpen] = useState<boolean>(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      setAnnouncement(OPEN_ANNOUNCEMENT);
      if (errorId !== undefined) {
        setInput(`I need help with error ${errorId}.`);
      }
    } else {
      setAnnouncement(CLOSE_ANNOUNCEMENT);
    }
  }

  // ------------------------------------------------------------------
  // Sending a message
  // ------------------------------------------------------------------

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      const text = input.trim();
      if (text.length === 0 || sending) {
        // Silently no-op — the button carries aria-disabled to signal
        // this state to assistive tech.
        return;
      }
      setSending(true);
      messageIdRef.current += 1;
      const userMsg: ChatMessage = {
        id: messageIdRef.current,
        role: "user",
        text,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      try {
        // Requirement 11: a bare Error ID (nothing else on the line) is
        // resolved against the owner-scoped `GET /errors/{errorId}`
        // endpoint instead of the tenant-isolated chatbot transport. Any
        // message with surrounding words falls through to the transport.
        const errorId = parseErrorIdQuery(text);
        const response: ChatbotResponse =
          errorId !== null
            ? await errorLookup(errorId)
            : await transport.send({
                sessionId,
                message: text,
                context,
              });
        messageIdRef.current += 1;
        const assistantMsg: ChatMessage = {
          id: messageIdRef.current,
          role: "assistant",
          text: response.text,
          citedDocs: response.citedDocs,
        };
        setMessages((prev) => [...prev, assistantMsg]);
      } catch {
        // The transport interface promises never to reject, but a real
        // MCP client could still fail in an edge case; surface a stub
        // system message rather than throwing into React.
        messageIdRef.current += 1;
        setMessages((prev) => [
          ...prev,
          {
            id: messageIdRef.current,
            role: "assistant",
            text: "Sorry — I couldn't reach the assistant. Try again in a moment.",
          },
        ]);
      } finally {
        setSending(false);
      }
    },
    [input, sending, transport, sessionId, context, errorLookup],
  );

  // ------------------------------------------------------------------
  // FocusTrap options
  // ------------------------------------------------------------------
  //
  // `initialFocus` resolves to a selector string so `focus-trap` can
  // locate the target within its own container. `setReturnFocus` points
  // at the FAB button owned by `ChatbotFAB` (Wave 2.6). `onDeactivate`
  // fires on `Esc`, on outside interaction (disallowed by default), and
  // on FocusTrap unmount — we route all three through `onClose` so the
  // parent controls the open flag.

  const focusTrapOptions = useMemo(
    () => ({
      escapeDeactivates: true,
      clickOutsideDeactivates: false,
      returnFocusOnDeactivate: true,
      // Skip the one-frame delay so tests observe focus synchronously.
      delayInitialFocus: false,
      onDeactivate: () => {
        onClose();
      },
      initialFocus: (): string => {
        return errorId !== undefined
          ? SEND_BUTTON_SELECTOR
          : MESSAGE_INPUT_SELECTOR;
      },
      // The FAB owns the trigger element; return focus there on close.
      setReturnFocus: (): string => FAB_SELECTOR,
      // Ensure the trap does not scroll the message input into view
      // during activation (jsdom throws on unimplemented scroll APIs).
      preventScroll: true,
      // `tabbable`'s default `displayCheck` calls `offsetParent`, which
      // returns `null` for every element inside a `position: fixed`
      // container in jsdom — the resulting empty tabbable list would
      // make `focus-trap` throw at activation. Disable the display
      // check so the container's semantic tabbables (input, buttons)
      // are always discoverable. In real browsers the visual layout
      // makes the check pass so this has no runtime effect.
      tabbableOptions: {
        displayCheck: "none" as const,
      },
    }),
    [onClose, errorId],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  const sendDisabled = sending || input.trim().length === 0;

  return (
    <>
      <AriaLive message={announcement} />
      {open ? (
        <FocusTrap focusTrapOptions={focusTrapOptions}>
          <div
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            data-chatbot-dock=""
            className="fixed w-[360px] h-[520px] bg-white shadow-xl rounded-lg border border-gray-200 flex flex-col overflow-hidden"
            style={{ bottom: "96px", right: "24px", zIndex: 46 }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h2 id={titleId} className="text-sm font-semibold text-gray-900">
                Ledgiventa assistant
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close chatbot"
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-800 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <span aria-hidden="true" className="text-lg leading-none">
                  ×
                </span>
              </button>
            </header>

            <div
              className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
              aria-label="Chat transcript"
              role="log"
              aria-live="off"
            >
              {messages.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Ask a question to get started. Answers will cite the
                  documentation pages they came from.
                </p>
              ) : (
                messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))
              )}
              {sending ? (
                <p
                  className="text-xs text-gray-500 italic"
                  data-testid="chatbot-sending-indicator"
                >
                  Assistant is typing…
                </p>
              ) : null}
            </div>

            <form
              onSubmit={handleSubmit}
              className="border-t border-gray-200 px-3 py-2 flex gap-2 bg-white"
            >
              <label htmlFor="chatbot-message-input" className="sr-only">
                Message
              </label>
              <input
                id="chatbot-message-input"
                data-chatbot-message-input=""
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                autoComplete="off"
                className="flex-1 h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="submit"
                data-chatbot-send-button=""
                aria-label="Send message"
                aria-disabled={sendDisabled}
                className={
                  sendDisabled
                    ? "h-9 px-3 bg-primary-600 text-white text-sm rounded-md opacity-50 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
                    : "h-9 px-3 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                }
              >
                Send
              </button>
            </form>
          </div>
        </FocusTrap>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Message bubble (module-local — small enough to inline, kept private)
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: ChatMessage;
}

/**
 * Renders a single chat message. Assistant messages that carry
 * `citedDocs[]` render one {@link CitationChip} per citation below the
 * message body.
 */
function MessageBubble(props: MessageBubbleProps): JSX.Element {
  const { message } = props;
  const isUser = message.role === "user";
  return (
    <div className={isUser ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          isUser
            ? "max-w-[80%] px-3 py-2 rounded-lg bg-primary-600 text-white text-sm"
            : "max-w-[80%] px-3 py-2 rounded-lg bg-gray-100 text-gray-900 text-sm"
        }
      >
        <p className="whitespace-pre-wrap break-words">{message.text}</p>
        {!isUser &&
        message.citedDocs !== undefined &&
        message.citedDocs.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {message.citedDocs.map((path) => (
              <CitationChip key={path} path={path} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
